import { isNil } from 'lodash';
import React, { useContext, useState } from 'react';
import { View as RNView, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBBottomSheetProvider } from '@shared/components/bottomSheet';
import { Styles } from '@shared/components/button';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody2Bold } from '@shared/components/typography';
import { Field } from '@shared/data/entity/fields/Field';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { useResponsiveFormContext } from '../ResponsiveFormContext';
import { SingleComponentForm } from './InputButtonForms/SingleComponentForm';

interface Props {
  component: MBAPI.WorkflowTemplate_components_InputButtonComponent;
  inputField: Field | null;
  workflowTemplateId: string;
  updateField: (componentId: string, field: Field | null) => void;
  style?: StyleProp<ViewStyle>;
}

export function InputButtonEditField({
  component,
  inputField,
  workflowTemplateId,
  updateField,
  style,
}: Props) {
  const responsiveForms = useResponsiveFormContext();
  const anchorName = `--input-button-edit-${component.id}`;
  const [showForm, setShowForm] = useState(false);
  const colors = useContext(ColorThemeContext);
  const workflowTemplate = useWorkflowTemplate(workflowTemplateId);

  const onPress = () => {
    setShowForm(prev => !prev);
  };

  return (
    <RNView
      style={[
        !responsiveForms && styles.buttonContainer,
        responsiveForms && styles.responsiveButtonContainer,
        style,
      ]}
    >
      <MBTouchableOpacity onPress={onPress}>
        <View style={{ anchorName }}>
          <MBView
            center
            paddingHorizontal={2.5}
            style={[
              Styles.button,
              Styles.buttonSizeSmall,
              {
                backgroundColor: responsiveForms
                  ? Colors.v2.transparent
                  : colors.button.backgroundInverted,
                borderColor: showForm ? Colors.v2.gray30 : colors.button.foregroundInverted,
                alignSelf: 'flex-start',
                width: responsiveForms ? 'auto' : '100%',
              },
            ]}
          >
            <MBTextBody2Bold
              numberOfLines={1}
              ellipsizeMode={'tail'}
              color={showForm ? Colors.v2.gray30 : colors.button.foregroundInverted}
            >
              {component.buttonText}
            </MBTextBody2Bold>
          </MBView>
        </View>
      </MBTouchableOpacity>
      {showForm && !isNil(workflowTemplate.workflowTemplate) && (
        <MBBottomSheetProvider>
          <SingleComponentForm
            fieldName={component.inputComponentId}
            field={inputField}
            anchorName={anchorName}
            workflowTemplate={workflowTemplate.workflowTemplate}
            onClose={() => setShowForm(false)}
            loading={workflowTemplate.loading}
            onUpdateField={updateField}
          />
        </MBBottomSheetProvider>
      )}
    </RNView>
  );
}

const styles = StyleSheet.create({
  buttonContainer: {
    paddingHorizontal: grid(3),
    paddingVertical: grid(1.75),
  },
  responsiveButtonContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
